<script setup lang="ts">
import { useMobile } from '@/composables/useMobile';
import PositionablePopoverDrawer from '@/core/dialogs/PositionablePopoverDrawer.vue';
import {
  canClosePositionablePopoverDrawer,
  findBestPopoverPosition,
  getPopoverContentHeightBudget,
  observePopoverPosition,
  type TPositionablePopoverDrawerCloseReason,
} from '@/core/dialogs/positionablePopover.utils';
import { calculateElementPosition } from '@/utils/dom.utils';
import { EKeyboardKey } from '@/utils/menu.utils';
import { onClickOutside, unrefElement } from '@vueuse/core';
import { EPosition } from 'common/enum/misc.enum';
import { computed, nextTick, onBeforeUnmount, ref, toRefs, useTemplateRef, watch } from 'vue';

defineOptions({ inheritAttrs: false });

type Overflow = { left: boolean; right: boolean; top: boolean; bottom: boolean; any: boolean; amount: number };
type AuthoredMaxSize = { maxWidth: string; maxHeight: string };

const emit = defineEmits<{
  hide: [];
  'mouseleave:content': [];
  'mouseenter:content': [];
  'update:position': [position: EPosition];
  positioned: [position: EPosition];
  'click:outside': [];
}>();

const props = withDefaults(
  defineProps<{
    anchorElement?: HTMLElement | null;
    autoFlip?: boolean;
    contentStyle?: string;
    dialogStyle?: string;
    disableClamping?: boolean;
    disableClickOutside?: boolean;
    flipStrategy?: 'both' | 'horizontal' | 'vertical';
    fullHeightMode?: boolean;
    fullWidth?: boolean;
    /** defers mounting the slot content until the first show() — for popovers with expensive content */
    lazyContent?: boolean;
    /** Presents the same selection content as a modal bottom drawer on mobile. */
    mobileDrawerMode?: boolean;
    mobileDrawerLabel?: string;
    mobileViewportMode?: boolean;
    /** Reserve room for fixed controls and a usable list, allowing overlap with the anchor on short screens. */
    minContentHeight?: number;
    offsetX?: number;
    offsetY?: number;
    position?: EPosition;
    /** Use content for a constrained flex layout with its own scrolling list/grid. */
    scrollMode?: 'popover' | 'content';
    shadow?: boolean;
  }>(),
  {
    anchorElement: null,
    autoFlip: true,
    contentStyle: '',
    dialogStyle: '',
    disableClickOutside: false,
    disableClamping: false,
    flipStrategy: 'horizontal',
    fullHeightMode: false,
    fullWidth: false,
    lazyContent: false,
    mobileDrawerMode: false,
    mobileDrawerLabel: undefined,
    mobileViewportMode: false,
    minContentHeight: 0,
    offsetX: 0,
    offsetY: 0,
    position: EPosition.BOTTOM_LEFT,
    scrollMode: 'popover',
    shadow: true,
  },
);
const {
  anchorElement,
  autoFlip,
  disableClamping,
  disableClickOutside,
  flipStrategy,
  fullWidth,
  offsetX,
  offsetY,
  position,
} = toRefs(props);
const { isMobile } = useMobile();

const popoverRef = useTemplateRef<HTMLDivElement | null>('popoverRef');
const dialogRefContent = useTemplateRef('dialogRefContent');
const mobileDrawerRef = useTemplateRef<InstanceType<typeof PositionablePopoverDrawer>>('mobileDrawerRef');
let stopPositionObserver: (() => void) | undefined;
let resolvedPosition: EPosition | undefined;
const authoredMaxSizeByElement = new WeakMap<HTMLElement, AuthoredMaxSize>();
const isOpen = ref<boolean>(false);
// once mounted, lazy content stays mounted so reopening is instant and slot refs stay stable
const contentMounted = ref(!props.lazyContent);
const VIEWPORT_MARGIN = 16;
const usesMobileDrawer = computed(() => props.mobileDrawerMode && isMobile.value);
// One layer scrolls: this wrapper, or the slot's list/grid in content mode.
// Content mode needs min-h-0 along the flex chain and shrink-0 on fixed headers/footers.
const contentLayoutClasses = computed(() =>
  props.scrollMode === 'content' ? 'flex flex-col overflow-hidden' : 'overflow-auto overscroll-contain',
);

const getDialogElement = (): HTMLElement | null => {
  return popoverRef.value;
};

const isOpeningBelow = (p: EPosition): boolean => {
  return p === EPosition.BOTTOM_CENTER || p === EPosition.BOTTOM_LEFT || p === EPosition.BOTTOM_RIGHT;
};

const isOpeningAbove = (p: EPosition): boolean => {
  return p === EPosition.TOP_CENTER || p === EPosition.TOP_LEFT || p === EPosition.TOP_RIGHT;
};

const getAvailableSpaceFromAnchor = (anchor: HTMLElement | null | undefined, finalPos: EPosition): number => {
  const vh = document.documentElement.clientHeight;
  if (!anchor) {
    return Math.max(0, vh - VIEWPORT_MARGIN * 2);
  }
  const ar = anchor.getBoundingClientRect();
  if (isOpeningBelow(finalPos)) {
    return Math.max(0, vh - VIEWPORT_MARGIN - ar.bottom - offsetY.value);
  }
  if (isOpeningAbove(finalPos)) {
    return Math.max(0, ar.top - VIEWPORT_MARGIN - offsetY.value);
  }
  return Math.max(0, vh - VIEWPORT_MARGIN * 2);
};

const getOverflow = (el: HTMLElement): Overflow => {
  const rect = el.getBoundingClientRect();
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;
  const left = rect.left < VIEWPORT_MARGIN;
  const right = rect.right > vw - VIEWPORT_MARGIN;
  const top = rect.top < VIEWPORT_MARGIN;
  const bottom = rect.bottom > vh - VIEWPORT_MARGIN;
  const amount =
    Math.max(0, VIEWPORT_MARGIN - rect.left) +
    Math.max(0, rect.right - vw + VIEWPORT_MARGIN) +
    Math.max(0, VIEWPORT_MARGIN - rect.top) +
    Math.max(0, rect.bottom - vh + VIEWPORT_MARGIN);
  return { left, right, top, bottom, any: left || right || top || bottom, amount };
};

const nudgeIntoViewport = (el: HTMLElement): void => {
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;
  const rect = el.getBoundingClientRect();

  const curTop = parseFloat(getComputedStyle(el).top || '0');
  const curLeft = parseFloat(getComputedStyle(el).left || '0');

  let nudgedTop = curTop;
  let nudgedLeft = curLeft;

  if (rect.left < VIEWPORT_MARGIN) nudgedLeft += VIEWPORT_MARGIN - rect.left;
  if (rect.right > vw - VIEWPORT_MARGIN) nudgedLeft -= rect.right - (vw - VIEWPORT_MARGIN);
  if (rect.top < VIEWPORT_MARGIN) nudgedTop += VIEWPORT_MARGIN - rect.top;
  if (rect.bottom > vh - VIEWPORT_MARGIN) nudgedTop -= rect.bottom - (vh - VIEWPORT_MARGIN);

  el.style.left = `${Math.round(nudgedLeft)}px`;
  el.style.top = `${Math.round(nudgedTop)}px`;
};

const assignCalculatedPosition = (p: EPosition): void => {
  const dialogElement = getDialogElement();
  if (!dialogElement) return;

  Object.assign(
    dialogElement.style,
    calculateElementPosition({
      anchorElement: anchorElement.value,
      element: dialogElement,
      offsetX: offsetX.value,
      offsetY: offsetY.value,
      fullWidth: fullWidth.value,
      position: p,
    }),
  );
};

const getAuthoredMaxSize = (el: HTMLElement): AuthoredMaxSize => {
  const cached = authoredMaxSizeByElement.get(el);
  if (cached) return cached;

  const style = getComputedStyle(el);
  const authoredMaxSize = {
    maxHeight: style.maxHeight,
    maxWidth: style.maxWidth,
  };
  authoredMaxSizeByElement.set(el, authoredMaxSize);
  return authoredMaxSize;
};

const getViewportBoundedMaxSize = (authoredSize: string, viewportSize: number): string => {
  const viewportMaxSize = `${viewportSize}px`;
  return !authoredSize || authoredSize === 'none' ? viewportMaxSize : `min(${authoredSize}, ${viewportMaxSize})`;
};

const clampToViewport = (
  el: HTMLElement,
  opts?: { contentEl?: HTMLElement | null; anchorEl?: HTMLElement | null; finalPos?: EPosition },
): void => {
  if (disableClamping.value) return;
  const { contentEl, anchorEl, finalPos } = opts ?? {};
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;

  const maxW = Math.max(0, vw - VIEWPORT_MARGIN * 2);
  const maxH = Math.max(0, vh - VIEWPORT_MARGIN * 2);
  const authoredMaxSize = getAuthoredMaxSize(el);

  el.style.maxWidth = getViewportBoundedMaxSize(authoredMaxSize.maxWidth, maxW);
  el.style.maxHeight = getViewportBoundedMaxSize(authoredMaxSize.maxHeight, maxH);

  if (contentEl) {
    const availableSpaceFromAnchor = getAvailableSpaceFromAnchor(anchorEl ?? null, finalPos ?? EPosition.BOTTOM_LEFT);
    const contentRect = contentEl.getBoundingClientRect();
    const dialogRect = el.getBoundingClientRect();

    const chromeAbove = Math.max(0, contentRect.top - dialogRect.top);
    const chromeBelow = Math.max(0, dialogRect.bottom - contentRect.bottom);

    let usableForContent = getPopoverContentHeightBudget({
      viewportHeight: maxH,
      availableSpaceFromAnchor,
      chromeHeight: chromeAbove + chromeBelow,
      minContentHeight: props.minContentHeight,
    });

    const cs = getComputedStyle(contentEl);
    const vInsets =
      parseFloat(cs.paddingTop) +
      parseFloat(cs.paddingBottom) +
      parseFloat(cs.borderTopWidth) +
      parseFloat(cs.borderBottomWidth);
    if (cs.boxSizing !== 'border-box') usableForContent = Math.max(0, usableForContent - vInsets);

    contentEl.style.maxHeight = getViewportBoundedMaxSize(getAuthoredMaxSize(contentEl).maxHeight, usableForContent);
  }

  if (finalPos) {
    assignCalculatedPosition(finalPos);
  }

  nudgeIntoViewport(el);
};

const flipH = (p: EPosition): EPosition => {
  switch (p) {
    case EPosition.TOP_LEFT:
      return EPosition.TOP_RIGHT;
    case EPosition.TOP_RIGHT:
      return EPosition.TOP_LEFT;
    case EPosition.BOTTOM_LEFT:
      return EPosition.BOTTOM_RIGHT;
    case EPosition.BOTTOM_RIGHT:
      return EPosition.BOTTOM_LEFT;
    case EPosition.LEFT:
      return EPosition.RIGHT;
    case EPosition.RIGHT:
      return EPosition.LEFT;
    default:
      return p;
  }
};

const flipV = (p: EPosition): EPosition => {
  switch (p) {
    case EPosition.TOP_CENTER:
      return EPosition.BOTTOM_CENTER;
    case EPosition.TOP_LEFT:
      return EPosition.BOTTOM_LEFT;
    case EPosition.TOP_RIGHT:
      return EPosition.BOTTOM_RIGHT;
    case EPosition.BOTTOM_CENTER:
      return EPosition.TOP_CENTER;
    case EPosition.BOTTOM_LEFT:
      return EPosition.TOP_LEFT;
    case EPosition.BOTTOM_RIGHT:
      return EPosition.TOP_RIGHT;
    default:
      return p;
  }
};

const applyPosition = (p: EPosition): void => {
  emit('update:position', p);
  assignCalculatedPosition(p);
};

const applyMobileViewportPosition = (dialogElement: HTMLElement): void => {
  Object.assign(dialogElement.style, {
    bottom: 'calc(88px + env(safe-area-inset-bottom))',
    height: 'auto',
    left: '8px',
    maxHeight: 'none',
    maxWidth: 'none',
    right: '8px',
    top: 'calc(8px + env(safe-area-inset-top))',
    width: 'auto',
  });

  if (dialogRefContent.value instanceof HTMLElement) {
    dialogRefContent.value.style.maxHeight = 'none';
  }
};

const clearMobileViewportPosition = (dialogElement: HTMLElement): void => {
  dialogElement.style.right = '';
  dialogElement.style.bottom = '';
  dialogElement.style.height = '';
  dialogElement.style.width = '';
  dialogElement.style.maxHeight = '';
  dialogElement.style.maxWidth = '';

  if (dialogRefContent.value instanceof HTMLElement) {
    dialogRefContent.value.style.maxHeight = '';
  }
};

const positionDialog = (preferredPosition: EPosition): void => {
  if (!isOpen.value) return;

  if (usesMobileDrawer.value) return;

  const dialogElement = getDialogElement();
  if (!dialogElement) return;

  if (props.mobileViewportMode && isMobile.value) {
    applyMobileViewportPosition(dialogElement);
    emit('positioned', position.value);
    return;
  }

  const currentAnchor = anchorElement.value;
  if (currentAnchor && !currentAnchor.isConnected) {
    hide();
    return;
  }

  clearMobileViewportPosition(dialogElement);

  let finalPos = preferredPosition;
  applyPosition(finalPos);

  const ov = getOverflow(dialogElement);
  if (autoFlip.value && ov.any) {
    const tryPositions: [EPosition, ...EPosition[]] = [finalPos];
    const horizontalOverflow = ov.left || ov.right;
    const verticalOverflow = ov.top || ov.bottom;
    const addCandidate = (candidate: EPosition): void => {
      if (!tryPositions.includes(candidate)) {
        tryPositions.push(candidate);
      }
    };

    if (flipStrategy.value !== 'vertical' && horizontalOverflow) {
      addCandidate(flipH(finalPos));
    }
    if (flipStrategy.value !== 'horizontal' && verticalOverflow) {
      addCandidate(flipV(finalPos));
    }
    if (flipStrategy.value === 'both' && horizontalOverflow && verticalOverflow) {
      addCandidate(flipV(flipH(finalPos)));
    }

    finalPos = findBestPopoverPosition(tryPositions, (candidate) => {
      applyPosition(candidate);
      return getOverflow(dialogElement).amount;
    });
    applyPosition(finalPos);
  }

  clampToViewport(dialogElement, {
    contentEl: dialogRefContent.value as HTMLElement | null,
    anchorEl: currentAnchor ?? null,
    finalPos,
  });
  resolvedPosition = finalPos;
  emit('positioned', finalPos);
};

const updateDialogPosition = (): void => positionDialog(position.value);

onClickOutside(dialogRefContent, (event) => {
  if (usesMobileDrawer.value) return;
  if (disableClickOutside.value) return;
  const anchorEl = unrefElement(anchorElement);

  if (
    (dialogRefContent.value && dialogRefContent.value.contains(event.target as Node)) ||
    (anchorEl && anchorEl.contains(event.target as Node))
  ) {
    return;
  }

  emit('click:outside');

  hide();
});

const hide = (): void => {
  void hideAndWait();
};

const hideAndWait = async (): Promise<void> => {
  emit('hide');
  if (!isOpen.value) return;

  isOpen.value = false;
  stopPositionObserver?.();
  stopPositionObserver = undefined;

  if (usesMobileDrawer.value) {
    await mobileDrawerRef.value?.hide();
    return;
  }

  const dialogElement = getDialogElement();
  if (!dialogElement) return;
  dialogElement.hidePopover();
};

const show = async (): Promise<void> => {
  if (!contentMounted.value) {
    contentMounted.value = true;
    await nextTick();
  }
  if (usesMobileDrawer.value) {
    await mobileDrawerRef.value?.show();
    isOpen.value = true;
    await nextTick();
    return;
  }

  const dialogElement = getDialogElement();
  if (!dialogElement) return;
  dialogElement.showPopover();
  isOpen.value = true;
  await nextTick();
  updateDialogPosition();
  updateSubscriptions();
};

const handleMobileDrawerDialogClose = (): void => {
  if (!isOpen.value) return;

  isOpen.value = false;
  emit('hide');
};

const handleMobileDrawerCloseRequest = (reason: TPositionablePopoverDrawerCloseReason): void => {
  if (!canClosePositionablePopoverDrawer(reason, disableClickOutside.value)) return;
  if (reason === 'backdrop') emit('click:outside');
  hide();
};

const handleKeydown = (event: KeyboardEvent): void => {
  if (event.key !== EKeyboardKey.Escape || !isOpen.value) return;

  if (event.defaultPrevented) {
    event.stopPropagation();
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  hide();
};

const updateSubscriptions = (): void => {
  stopPositionObserver?.();
  stopPositionObserver = undefined;
  const dialogElement = getDialogElement();
  if (!isOpen.value || usesMobileDrawer.value || !dialogElement) return;

  stopPositionObserver = observePopoverPosition(dialogElement, anchorElement.value, (reselectPosition) => {
    // Filtering must resize toward the anchor, preferring the side already chosen while it still fits.
    positionDialog(reselectPosition ? position.value : (resolvedPosition ?? position.value));
  });
};

onBeforeUnmount(() => {
  stopPositionObserver?.();
});

defineExpose({
  show,
  hide,
  hideAndWait,
  getDialogElement,
  isOpen,
  updateDialogPosition,
});

watch([anchorElement, offsetX, offsetY, position, fullWidth, isMobile], () => {
  if (isOpen.value) updateDialogPosition();
});

watch(anchorElement, updateSubscriptions, { flush: 'post' });

watch(usesMobileDrawer, () => {
  if (isOpen.value) hide();
});
</script>

<template>
  <!-- Apply flex only while open to preserve the browser's native hidden state. -->
  <div
    v-if="!usesMobileDrawer"
    ref="popoverRef"
    popover="manual"
    v-bind="$attrs"
    :class="[
      'fixed',
      '[&:popover-open]:flex [&:popover-open]:flex-col overflow-hidden',
      'p-0 m-0',
      'rounded-panel outline-transparent',
      'bg-white',
      shadow && 'shadow-popover',
      fullHeightMode && 'overflow-visible',
      dialogStyle,
    ]"
    @keydown="handleKeydown"
  >
    <slot v-if="contentMounted" name="above-content"></slot>
    <div
      ref="dialogRefContent"
      class="min-h-0 rounded-panel"
      :class="[contentLayoutClasses, fullHeightMode && 'h-full', contentStyle]"
      @mouseenter="emit('mouseenter:content')"
      @mouseleave="emit('mouseleave:content')"
    >
      <slot v-if="contentMounted" :mobile-drawer="false"></slot>
    </div>
  </div>

  <PositionablePopoverDrawer
    v-else
    ref="mobileDrawerRef"
    :accessible-label="props.mobileDrawerLabel"
    @close="handleMobileDrawerDialogClose"
    @request-close="handleMobileDrawerCloseRequest"
  >
    <template #above-content>
      <slot v-if="contentMounted" name="above-content"></slot>
    </template>
    <div
      ref="dialogRefContent"
      class="min-h-0 flex-1"
      :class="[contentLayoutClasses, contentStyle]"
      @mouseenter="emit('mouseenter:content')"
      @mouseleave="emit('mouseleave:content')"
    >
      <slot v-if="contentMounted" :mobile-drawer="true"></slot>
    </div>
  </PositionablePopoverDrawer>
</template>
